import { FC, useRef, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import { Box, CardContent, ClickAwayListener, IconButton, Paper, Popper, Stack, Tab, Tabs, Typography } from '@mui/material';
import ProfileLinksList from './ProfileLinksList';
import Avatar from '../../../../../components/@extended/Avatar';
import MainCard from '../../../../../components/MainCard';
import Transitions from '../../../../../components/@extended/Transitions';
import { ThemeMode } from '../../../../../types/config';
import { useSignOut } from '../../../../../auth/useSignOut';
import { FormattedMessage } from 'react-intl';
import { OodThemeMode, useThemeMode } from '../../../../../themes/themeModeProvider';
import { DarkMode, DarkModeOutlined, LightMode, LightModeOutlined } from '@mui/icons-material';
import { getNightOrDayPalette } from '../../../../../utils/getNightOrDayPalette';
import { useAuthSignedIn } from '../../../../../auth/useAuth';
import { useCookies } from 'react-cookie';

const Profile: FC = () => {
	const theme = useTheme();
	const { me } = useAuthSignedIn();
	const signOut = useSignOut((): void => {
		console.log('sign out error'); // todo show toast
	});
	const [{ ood_impersonated_token }] = useCookies(['ood_impersonated_token']);

	const handleLogout = async () => {
		signOut();
	};

	const anchorRef = useRef<any>(null);
	const [open, setOpen] = useState(false);
	const handleToggle = () => {
		setOpen((prevOpen) => !prevOpen);
	};

	const handleClose = (event: MouseEvent | TouchEvent) => {
		if (anchorRef.current && anchorRef.current.contains(event.target)) {
			return;
		}
		setOpen(false);
	};

	const { oodMode, setOodMode, mode } = useThemeMode();

	const handleThemeChange = (event: React.SyntheticEvent, newValue: OodThemeMode) => {
		setOodMode(newValue);
	};

	const iconBackColorOpen = theme.palette.mode === ThemeMode.DARK ? 'grey.200' : 'grey.300';

	return (
		<Box sx={{ flexShrink: 0, ml: 0.75, borderRadius: 2 }}>
			<IconButton
				sx={{
					p: 0.5,
					bgcolor: open ? iconBackColorOpen : 'transparent',
					borderRadius: 1,
					'&:hover': { bgcolor: theme.palette.mode === ThemeMode.DARK ? 'secondary.light' : 'secondary.lighter' },
					'&:focus-visible': {
						outline: `2px solid ${theme.palette.secondary.dark}`,
						outlineOffset: 2,
					},
				}}
				ref={anchorRef}
				aria-controls={open ? 'profile-grow' : undefined}
				aria-haspopup={'true'}
				onClick={handleToggle}
			>
				<Avatar alt={me.name || "that's me"} src={me.avatar?.url ?? undefined} sx={{ width: 32, height: 32 }} />
			</IconButton>
			<Popper
				placement={'bottom-end'}
				open={open}
				anchorEl={anchorRef.current}
				role={undefined}
				transition
				disablePortal
				sx={{ zIndex: 150 }}
				popperOptions={{
					modifiers: [
						{
							name: 'offset',
							options: {
								offset: [0, 9],
							},
						},
					],
				}}
			>
				{({ TransitionProps }) => (
					<Transitions type={'grow'} position={'top-right'} in={open} {...TransitionProps}>
						<Paper
							sx={{
								boxShadow: theme.customShadows.z1,
								width: 290,
								minWidth: 240,
								maxWidth: 290,
								[theme.breakpoints.down('md')]: {
									maxWidth: 250,
								},
								borderRadius: 2,
							}}
						>
							<ClickAwayListener onClickAway={handleClose}>
								<MainCard sx={{ borderRadius: 2 }} elevation={0} border={false} content={false}>
									<CardContent sx={{ px: 2.5, pt: 3, borderBottom: 1, borderColor: theme.palette[getNightOrDayPalette(theme)][200] }}>
										<Stack direction={'row'} spacing={1.25} alignItems={'center'}>
											<Avatar alt={me.name || "that's me"} src={me.avatar?.url ?? undefined} sx={{ width: 40, height: 40 }} />
											<Stack>
												{ood_impersonated_token && (
													<Typography variant={'body2'}>
														<FormattedMessage id={'common.impersonated'} />
													</Typography>
												)}
												<Typography variant={'h6'}>{me.name || <FormattedMessage id={'common.not-available'} />}</Typography>
												<Typography variant={'body2'} color={'textSecondary'}>
													{me.person?.email ?? <FormattedMessage id={'common.not-available'} />}
												</Typography>
											</Stack>
										</Stack>
									</CardContent>

									<ProfileLinksList handleLogout={handleLogout} onClose={() => setOpen(false)} />

									<CardContent
										sx={{
											borderRadius: 2,
											'&:last-child': { p: 1.5 },
											borderTop: 1,
											borderColor: theme.palette[getNightOrDayPalette(theme)][200],
										}}
									>
										<Tabs
											value={oodMode}
											variant={'standard'}
											onChange={handleThemeChange}
											scrollButtons={false}
											sx={{
												backgroundColor:
													mode === ThemeMode.DARK
														? theme.palette[getNightOrDayPalette(theme)][0]
														: theme.palette[getNightOrDayPalette(theme)][100],
												borderRadius: 2,
												'.MuiTabs-root': { display: 'flex', alignItems: 'center' },
												'.MuiTabs-flexContainer': {
													height: '100%',
													alignItems: 'center',
												},
												'.MuiTabs-scroller': {
													width: '100%',
													height: 40,
													px: 1,
													alignSelf: 'center',
												},
												'.MuiTabs-indicator': {
													top: 4,
													height: 32,
													borderRadius: 2,
													backgroundColor:
														mode === ThemeMode.DARK
															? theme.palette[getNightOrDayPalette(theme)][100]
															: theme.palette[getNightOrDayPalette(theme)][0],
													boxShadow: `0 1px 3px 0 ${
														mode === ThemeMode.DARK
															? theme.palette[getNightOrDayPalette(theme)][0]
															: theme.palette[getNightOrDayPalette(theme)][300]
													}`,
												},
												'.MuiButtonBase-root': {
													minHeight: 30,
													minWidth: '33.333%',
													backgroundColor:
														mode === ThemeMode.DARK
															? theme.palette[getNightOrDayPalette(theme)][0]
															: theme.palette[getNightOrDayPalette(theme)][100],
													p: 0,
													'.MuiSvgIcon-root': { zIndex: 1 },
												},
											}}
										>
											<Tab value={OodThemeMode.LIGHT} icon={oodMode === OodThemeMode.LIGHT ? <LightMode /> : <LightModeOutlined />} />
											<Tab value={OodThemeMode.DARK} icon={oodMode === OodThemeMode.DARK ? <DarkMode /> : <DarkModeOutlined />} />
											<Tab
												value={OodThemeMode.SYSTEM}
												label={
													<Typography zIndex={1}>
														<FormattedMessage id={'profile.theme.default'} />
													</Typography>
												}
												sx={{ backgroundColor: theme.palette[getNightOrDayPalette(theme)][0] }}
											/>
										</Tabs>
									</CardContent>
								</MainCard>
							</ClickAwayListener>
						</Paper>
					</Transitions>
				)}
			</Popper>
		</Box>
	);
};

export default Profile;
